import { ComponentProps, ReactNode } from 'react';
import { FieldPath, FieldValues, useFormContext } from 'react-hook-form';
import { cn } from '../../utils/cn';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '../form/form-primitives';
import { Select } from '../select/select';
import { Stack } from '../stack/stack';

type FormSelectFieldProps<TFieldValues extends FieldValues> = {
  label: string;
  hideLabel?: boolean;
  badge?: string;
  inputName: FieldPath<TFieldValues>;
  placeholder?: string;
  isOptional?: boolean;
  isDisabled?: boolean;
  searchBoxConfig?: ComponentProps<typeof Select>['searchBoxConfig'];
  isTriggerFullWidth?: boolean;
  triggerClassName?: string;
  children: ReactNode;
};

export const FormSelectField = <TFieldValues extends FieldValues>({
  label,
  hideLabel = false,
  badge,
  inputName,
  placeholder,
  isOptional,
  searchBoxConfig = { visible: false },
  isTriggerFullWidth = false,
  triggerClassName,
  children,
}: FormSelectFieldProps<TFieldValues>) => {
  const { control } = useFormContext();

  return (
    <FormField
      control={control}
      name={inputName}
      render={({ field, formState }) => {
        return (
          <FormItem onBlur={field.onBlur}>
            <Stack space={1} align="stretch" className="relative">
              {badge && <p className="absolute top-1 right-3 text-[10px] font-medium text-muted-foreground">{badge}</p>}

              <FormControl>
                <Select
                  aria-label={label}
                  required={!isOptional}
                  placeholder={placeholder}
                  triggerLabel={
                    <FormLabel className={cn('text-xs font-medium text-muted-foreground', { hidden: hideLabel })}>
                      {label}
                    </FormLabel>
                  }
                  value={field.value}
                  onValueChange={field.onChange}
                  triggerClassName={cn(
                    'rounded-lg border border-border bg-white px-4 py-3 text-base font-medium text-black [&_.account-logo]:h-4 [&_.account-logo]:w-4 [&_.account-type]:hidden',
                    { 'border-destructive': !!formState.errors[inputName] },
                    triggerClassName,
                  )}
                  searchBoxConfig={searchBoxConfig}
                  isTriggerFullWidth={isTriggerFullWidth}
                >
                  {children}
                </Select>
              </FormControl>

              <FormMessage className="w-full text-right" />
            </Stack>
          </FormItem>
        );
      }}
    />
  );
};
